import React from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { isFeatureEnabled, type FeatureFlagMap, type FeatureKey } from './featureFlags';
import { isConfigurationProfile, type ConfigurationProfile } from './configurationProfile';
import { isExternalLinks, type ExternalLinks } from './ipAnchor/coreIdentityLinks';

interface EnvironmentContextType {
  envName: string | null;
  baseUrl: string | null;
  msApprovalsPortalUrl: string | null;
  /** Documentation and support links from the server's ExternalLinks configuration. */
  externalLinks: ExternalLinks | null;
  features: Features;
  flags: FeatureFlagMap;
  flagsReady: boolean;
  configurationProfile: ConfigurationProfile | null;
}

/**
 * Non-flag status markers from /api/config/environment. These are not capability gates — they
 * describe the state of something already visible. Real feature flags live in `flags`, sourced
 * from the catalog via /api/features.
 */
interface Features {
  /** Change Radar is serving sample signal data rather than live telemetry. */
  changeRadarPreview: boolean;
}

export type { Features };

/** Upper bound on how long a readiness-gated page may wait for /api/features before failing closed. */
const FLAG_FETCH_TIMEOUT_MS = 10_000;

// Change Radar's marker is a caution, not a capability, so it fails safe in the opposite direction
// to a feature gate: if /api/config/environment has not resolved yet or fails outright, warn rather
// than present sample signals as if they were telemetry. A stale warning is recoverable; a missing
// one is not. The server response corrects this either way.
const defaultFeatures: Features = { changeRadarPreview: true };

const EnvironmentContext = React.createContext<EnvironmentContextType>({ envName: null, baseUrl: null, msApprovalsPortalUrl: null, externalLinks: null, features: defaultFeatures, flags: {}, flagsReady: false, configurationProfile: null });

export const useEnvironment = () => React.useContext(EnvironmentContext);

/** Reads one feature flag. Visibility only — the server enforces the same flag independently. */
export const useFeature = (key: FeatureKey): boolean => {
  const { flags } = useEnvironment();
  return isFeatureEnabled(flags, key);
};

/**
 * True once GET /api/features has settled, whether it succeeded or not.
 *
 * Only needed where a flag selects *which* call to make rather than whether to show something. A
 * flag reads false until it loads, so acting on it immediately would fire the wrong request and
 * then correct itself — visible as a spurious failure. Components that merely show or hide a
 * control should ignore this and let the default-off value do its job.
 *
 * Settling on failure is deliberate: flags are all-false in that case, which is the defined
 * fail-closed state, and waiting for a response that is not coming would hang the caller forever.
 */
export const useFeaturesReady = (): boolean => useEnvironment().flagsReady;

export const EnvironmentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [envName, setEnvName] = React.useState<string | null>(null);
  const [baseUrl, setBaseUrl] = React.useState<string | null>(null);
  const [msApprovalsPortalUrl, setMsApprovalsPortalUrl] = React.useState<string | null>(null);
  const [externalLinks, setExternalLinks] = React.useState<ExternalLinks | null>(null);
  const [features, setFeatures] = React.useState<Features>(defaultFeatures);
  const [flags, setFlags] = React.useState<FeatureFlagMap>({});
  const [flagsReady, setFlagsReady] = React.useState(false);
  const [configurationProfile, setConfigurationProfile] = React.useState<ConfigurationProfile | null>(null);
  const [configError, setConfigError] = React.useState(false);
  const [configAttempt, setConfigAttempt] = React.useState(0);

  React.useEffect(() => {
    const abort = new AbortController();
    let active = true;
    const timer = window.setTimeout(() => {
      abort.abort();
      if (active) setConfigError(true);
    }, FLAG_FETCH_TIMEOUT_MS);
    setConfigError(false);
    fetch('/api/config/environment', { credentials: 'same-origin', signal: abort.signal })
      .then((res) => {
        if (!res.ok) throw new Error('Environment configuration unavailable');
        return res.json();
      })
      .then((data) => {
        if (!active || abort.signal.aborted) return;
        if (!isConfigurationProfile(data?.configurationProfile)) {
          throw new Error('Environment configuration profile is missing or invalid');
        }
        // Children render only once the profile is set, so links must be in place first.
        if (!isExternalLinks(data?.externalLinks)) {
          throw new Error('Environment configuration links are missing or invalid');
        }
        setExternalLinks(data.externalLinks);
        setEnvName(data.environment);
        if (data.baseUrl) setBaseUrl(data.baseUrl);
        if (data.msApprovalsPortalUrl) setMsApprovalsPortalUrl(data.msApprovalsPortalUrl);
        if (data.features) setFeatures({
          changeRadarPreview: !!data.features.changeRadarPreview,
        });

        // Load Microsoft Clarity if project ID is configured
        if (data.clarityProjectId) {
          loadClarity(data.clarityProjectId);
        }
        setConfigurationProfile(data.configurationProfile);
      })
      .catch(() => { if (active) setConfigError(true); })
      .finally(() => window.clearTimeout(timer));
    return () => { active = false; window.clearTimeout(timer); abort.abort(); };
  }, [configAttempt]);

  // Flags are fetched separately from environment config: they change on a different cadence, the
  // payload is cacheable on its own, and a failure here must leave every gate closed rather than
  // taking the whole environment context down with it.
  React.useEffect(() => {
    // Readiness gates rendering, so it has to settle even if the response never arrives. A hung
    // socket without this abort leaves a readiness-gated page on its skeleton indefinitely; the
    // timeout converts that into the ordinary failure path, which is all-flags-false.
    const abort = new AbortController();
    let active = true;
    const timer = window.setTimeout(() => abort.abort(), FLAG_FETCH_TIMEOUT_MS);
    fetch('/api/features', { credentials: 'same-origin', signal: abort.signal })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (active && data && typeof data === 'object') setFlags(data as FeatureFlagMap);
      })
      .catch(() => {})
      .finally(() => {
        window.clearTimeout(timer);
        if (active) setFlagsReady(true);
      });
    return () => { active = false; window.clearTimeout(timer); abort.abort(); };
  }, []);

  const value = React.useMemo(() => ({ envName, baseUrl, msApprovalsPortalUrl, externalLinks, features, flags, flagsReady, configurationProfile }), [envName, baseUrl, msApprovalsPortalUrl, externalLinks, features, flags, flagsReady, configurationProfile]);

  return (
    <EnvironmentContext.Provider value={value}>
      {configurationProfile ? children : (
        <Box component="main" sx={{ p: 4, maxWidth: 640, mx: 'auto' }}>
          {configError ? (
            <Alert severity="error" action={
              <Button color="inherit" onClick={() => { setConfigError(false); setConfigAttempt((attempt) => attempt + 1); }}>
                Retry
              </Button>
            }>
              AskIAM could not load its configuration. Please retry to continue.
            </Alert>
          ) : (
            <Box role="status" aria-live="polite" sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} aria-label="Loading configuration" />
              <Typography>Loading AskIAM configuration…</Typography>
            </Box>
          )}
        </Box>
      )}
    </EnvironmentContext.Provider>
  );
};

function loadClarity(projectId: string) {
  if ((window as any).clarity) return; // Already loaded

  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
    const t = l.createElement(r) as HTMLScriptElement;
    t.async = true;
    t.src = 'https://www.clarity.ms/tag/' + i;
    const y = l.getElementsByTagName(r)[0];
    y.parentNode?.insertBefore(t, y);
  })(window, document, 'clarity', 'script', projectId);
}
